import type { paths } from "../types/fulfilment-api.generated";
import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

type APIPackage =
  paths["/api/v1/packages/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["package"];

export type Package = APIPackage;

// Extended type with computed featureCodes and defaultSiteStructureId (not yet in generated types)
export type PackageWithFeatureCodes = Package & {
  featureCodes: string[];
  defaultSiteStructureId?: string | null;
};

export type MetadataWarning = {
  field: string;
  message: string;
  required_fields?: string[];
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Enriches a Package with computed featureCodes property.
 * Handles both cases:
 * - Response has featureCodes directly (update endpoint)
 * - Response has features array (get endpoints)
 */
export function enrichWithFeatureCodes(pkg: Package): PackageWithFeatureCodes {
  return {
    ...pkg,
    featureCodes:
      (pkg as PackageWithFeatureCodes).featureCodes ?? pkg.features?.map(f => f.code) ?? [],
  };
}

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

export type PackagesListResponse =
  paths["/api/v1/packages"]["get"]["responses"]["200"]["content"]["application/json"];
export type PackageSingleResponse =
  paths["/api/v1/packages/{id}"]["get"]["responses"]["200"]["content"]["application/json"];
type GeneratedCreatePackageRequest =
  paths["/api/v1/packages"]["post"]["requestBody"]["content"]["application/json"];
type GeneratedUpdatePackageRequest =
  paths["/api/v1/packages/{id}"]["put"]["requestBody"]["content"]["application/json"];

export type CreatePackageRequest = GeneratedCreatePackageRequest & {
  defaultSiteStructureId?: string | null;
};
export type UpdatePackageRequest = GeneratedUpdatePackageRequest & {
  defaultSiteStructureId?: string | null;
};

export type PackageVersionResponse = {
  package: PackageWithFeatureCodes;
  oldVersion: {
    id: string;
    version: number;
    subscriptionCount: number;
    isActive: boolean;
  };
  warnings?: MetadataWarning[];
  requestId: string | null;
};

// ============================================================================
// Service
// ============================================================================

export const packages = {
  getAll: async (filters?: {
    productCode?: string;
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<Omit<PackagesListResponse, "packages"> & { packages: PackageWithFeatureCodes[] }> => {
    const params = new URLSearchParams();
    if (filters?.productCode) params.append("productCode", filters.productCode);
    if (filters?.isActive !== undefined) params.append("isActive", filters.isActive.toString());
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<PackagesListResponse>(`/v1/fulfilment/packages${query}`);

    return {
      ...response,
      packages: (response.packages as unknown as Package[]).map(enrichWithFeatureCodes),
    };
  },

  getById: async (id: string): Promise<PackageWithFeatureCodes> => {
    const response = await api.get<PackageSingleResponse>(`/v1/fulfilment/packages/${id}`);
    return enrichWithFeatureCodes(response.package);
  },

  create: async (data: CreatePackageRequest): Promise<PackageWithFeatureCodes> => {
    const response = await api.post<PackageSingleResponse>("/v1/fulfilment/packages", data);
    return enrichWithFeatureCodes(response.package);
  },

  update: async (id: string, data: UpdatePackageRequest): Promise<PackageWithFeatureCodes> => {
    const response = await api.put<PackageSingleResponse>(`/v1/fulfilment/packages/${id}`, data);
    return enrichWithFeatureCodes(response.package);
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/fulfilment/packages/${id}`);
  },

  version: async (
    id: string,
    options?: { featureCodes?: string[] },
  ): Promise<PackageVersionResponse> => {
    const response = await api.post<Omit<PackageVersionResponse, "package"> & { package: Package }>(
      `/v1/fulfilment/packages/${id}/version`,
      options || {},
    );
    return {
      ...response,
      package: enrichWithFeatureCodes(response.package),
    };
  },

  getVersionsByProductCode: async (productCode: string): Promise<PackageWithFeatureCodes[]> => {
    const response = await api.get<{ versions: Package[]; requestId: string | null }>(
      `/v1/fulfilment/packages/${productCode}/versions`,
    );
    return response.versions.map(enrichWithFeatureCodes);
  },
};
